<template>
  <div class="py-4 space-y-8">
    <!-- Titre principal -->
    <div class="text-center mb-8">
      <h1 class="text-xl font-bold text-foreground mb-3">
        {{ $t('comparison.info.title') }}
      </h1>
      <p class="text-base text-muted-foreground leading-relaxed max-w-sm mx-auto">
        {{ $t('comparison.info.description') }}
      </p>
    </div>

    <!-- Section des types -->
    <div class="space-y-6">
      <!-- Comparaison Équitable -->
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <div class="w-3 h-3 rounded-full bg-blue-500"></div>
          <h3 class="text-base font-semibold text-foreground">{{ $t('comparison.info.equitable.title') }}</h3>
        </div>
        <p class="text-muted-foreground leading-relaxed ml-6 mb-3">
          {{ $t('comparison.info.equitable.description') }}
        </p>
        <ul class="text-muted-foreground leading-relaxed ml-6 space-y-1">
          <li>• {{ $t('comparison.info.equitable.feature1') }}</li>
          <li>• {{ $t('comparison.info.equitable.feature2') }}</li>
          <li>• {{ $t('comparison.info.equitable.feature3') }}</li>
        </ul>
        <div class="ml-6 mt-3 p-3 rounded-lg comp-tip-blue">
          <p class="text-sm font-medium">
            {{ $t('comparison.info.equitable.tip') }}
          </p>
        </div>
      </div>

      <!-- Comparaison Complète -->
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <div class="w-3 h-3 rounded-full bg-purple-500"></div>
          <h3 class="text-base font-semibold text-foreground">{{ $t('comparison.info.complete.title') }}</h3>
        </div>
        <p class="text-muted-foreground leading-relaxed ml-6 mb-3">
          {{ $t('comparison.info.complete.description') }}
        </p>
        <ul class="text-muted-foreground leading-relaxed ml-6 space-y-1">
          <li>• {{ $t('comparison.info.complete.feature1') }}</li>
          <li>• {{ $t('comparison.info.complete.feature2') }}</li>
          <li>• {{ $t('comparison.info.complete.feature3') }}</li>
        </ul>
        <div class="ml-6 mt-3 p-3 rounded-lg comp-tip-purple">
          <p class="text-sm font-medium">
            {{ $t('comparison.info.complete.tip') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Dates réelles -->
    <div class="bg-muted p-4 rounded-lg">
      <h4 class="font-semibold text-foreground mb-2">{{ $t('comparison.info.periods.title') }}</h4>
      <p class="text-sm text-muted-foreground leading-relaxed mb-3">
        {{ $t('comparison.info.periods.current') }} <strong class="text-foreground">{{ currentPeriod }}</strong>
      </p>
      <ul class="text-sm text-muted-foreground space-y-2">
        <li>• <strong class="text-foreground">{{ $t('comparison.info.periods.equitable') }}</strong> {{ equitablePreviousPeriod }}</li>
        <li>• <strong class="text-foreground">{{ $t('comparison.info.periods.complete') }}</strong> {{ completePreviousPeriod }}</li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  currentStartDate?: Date
  currentEndDate?: Date
  equitableStartDate?: Date
  equitableEndDate?: Date
  equitablePreviousStartDate?: Date
  equitablePreviousEndDate?: Date
  previousStartDate?: Date
  previousEndDate?: Date
}

const props = withDefaults(defineProps<Props>(), {})
const { t } = useI18n()

const formatPeriod = (startDate?: Date, endDate?: Date): string => {
  if (!startDate || !endDate) return t('data.unavailable')
  
  const start = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(startDate)
  
  const end = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(endDate)
  
  return `du ${start} au ${end}`
}

const currentPeriod = computed(() => formatPeriod(props.currentStartDate, new Date()))
const equitablePreviousPeriod = computed(() => formatPeriod(props.equitablePreviousStartDate, props.equitablePreviousEndDate))
const completePreviousPeriod = computed(() => formatPeriod(props.previousStartDate, props.previousEndDate))
</script>

<style scoped>
.comp-tip-blue {
  background: rgba(59, 130, 246, 0.08);
  color: #1D4ED8;
}
:is(.dark *).comp-tip-blue {
  background: rgba(96, 165, 250, 0.14);
  color: #93C5FD;
}

.comp-tip-purple {
  background: rgba(168, 85, 247, 0.08);
  color: #6D28D9;
}
:is(.dark *).comp-tip-purple {
  background: rgba(192, 132, 252, 0.14);
  color: #D8B4FE;
}
</style>